import { StatusCodes } from 'http-status-codes';
import type { AxiosError, AxiosResponse } from 'axios';
import { call, put, fork, takeEvery } from 'redux-saga/effects';

import history from '@/core/customHistory.ts';
import AppRoutes from 'app/utils/routes.ts';
import type { UserDto } from '@/api-gen/api.schemas';
import { LoadingStatus } from 'app/store/types.ts';
import {
  userGetCurrentUser,
  accountAllowRecord,
  accountEditUserAudioSettings,
  userCreateOrganizationInvitation,
  userInviteMultipleUsers
} from '@/api-gen/api.ts';
import { UserActions } from 'app/actions/user/userAction.ts';
import logger from 'app/utils/logger.ts';
import {
  vadThreshold,
  silenceThreshold,
  smartTurnEnabled,
  energyGateEnabled,
  energyGateThresholdDb,
  defaultValues as audioDefaults,
  legacyEnergyGateSettings,
  clearLegacyEnergyGateSettings,
  VAD_THRESHOLD_MAX
} from '@/features/audio';
import { currentUser } from '@/features/user';
import { isHeiDomain as checkIsHeiDomain } from 'app/utils/heiDomain';
import appInsights from 'app/components/AppInsights.tsx';
import { getClientCorrelationId } from 'app/utils/diagnostics.ts';

const getNow = () => (typeof performance === 'undefined' ? Date.now() : performance.now());

const getRequestIdFromHeaders = (headers?: Record<string, unknown>): string | undefined => {
  if (!headers) return undefined;

  return (headers['x-requestid'] as string) || (headers['x-request-id'] as string) || (headers['request-id'] as string);
};

const getNetworkDetails = () => {
  if (typeof navigator === 'undefined') return {};

  const connection = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection;

  return {
    isOnline: navigator.onLine,
    effectiveType: connection?.effectiveType
  };
};

const trackCurrentUserEvent = (
  name: string,
  properties: Record<string, string | number | boolean | undefined>,
  durationMs: number
) => {
  appInsights.trackEvent({
    name,
    properties: {
      ...properties,
      environment: process.env.NODE_ENV,
      hostname: typeof window === 'undefined' ? undefined : window.location.hostname,
      path: typeof window === 'undefined' ? undefined : window.location.pathname,
      ...getNetworkDetails()
    },
    measurements: { durationMs }
  });
};

/**
 * The energy gate used to be stored in localStorage. It now lives on the user
 * profile, so the first time we see a profile still holding the backend
 * defaults we push whatever the user had configured locally.
 *
 * The flag is only set once the settings are known to be on the profile, so a
 * failed request is retried on the next current-user load instead of silently
 * dropping the user's setting.
 */
let hasMigratedEnergyGate = false;
let isMigratingEnergyGate = false;

function* migrateLegacyEnergyGateSettings(userData: UserDto) {
  if (hasMigratedEnergyGate || isMigratingEnergyGate || !legacyEnergyGateSettings) {
    return;
  }

  // The user has already saved their audio settings since the switch, so the
  // profile — not the stale localStorage payload — is authoritative.
  const backendIsUntouched =
    (userData.energyGateEnabled ?? audioDefaults.energyGateEnabled) === audioDefaults.energyGateEnabled &&
    (userData.energyGateThresholdDb ?? audioDefaults.energyGateThresholdDb) === audioDefaults.energyGateThresholdDb;

  if (!backendIsUntouched) {
    hasMigratedEnergyGate = true;
    clearLegacyEnergyGateSettings();

    return;
  }

  isMigratingEnergyGate = true;

  // Apply locally first so the gate keeps working for this session even if the
  // request fails; the localStorage payload is kept so it can be retried.
  energyGateEnabled.value = legacyEnergyGateSettings.energyGateEnabled;
  energyGateThresholdDb.value = legacyEnergyGateSettings.energyGateThresholdDb;

  try {
    // speechThreshold/speechWaitingTime/smartTurnEnabled are not optional server
    // side, so they must be echoed back or they would be reset to their defaults.
    yield call(accountEditUserAudioSettings, {
      speechThreshold: userData.speechThreshold,
      speechWaitingTime: userData.speechWaitingTime,
      smartTurnEnabled: userData.smartTurnEnabled,
      energyGateEnabled: legacyEnergyGateSettings.energyGateEnabled,
      energyGateThresholdDb: legacyEnergyGateSettings.energyGateThresholdDb
    });

    hasMigratedEnergyGate = true;
    clearLegacyEnergyGateSettings();
  } catch (error) {
    // Keep the localStorage payload so the next current-user load can retry.
    logger.error('Failed to migrate energy gate settings to the user profile', error);
  } finally {
    isMigratingEnergyGate = false;
  }
}

export function* getCurrentUser() {
  const requestStart = getNow();
  yield put(UserActions.setCurrentUserLoadingStatus(LoadingStatus.Loading));
  yield put(UserActions.setCurrentUserLoadError(null));

  try {
    const response: AxiosResponse<UserDto> = yield call(userGetCurrentUser);

    if (!response?.status) {
      const requestId = getRequestIdFromHeaders(response?.headers) ?? getClientCorrelationId();
      trackCurrentUserEvent(
        'CurrentUserLoadFailed',
        {
          reason: 'missing_status',
          requestId
        },
        getNow() - requestStart
      );
      yield put(
        UserActions.setCurrentUserLoadError({
          requestId,
          timestamp: new Date().toISOString()
        })
      );
      return yield put(UserActions.setCurrentUserLoadingStatus(LoadingStatus.Failed));
    }

    switch (response.status) {
      case StatusCodes.OK: {
        const userData = response.data;

        yield put(UserActions.setCurrentUser(userData));
        currentUser.value = userData;
        const speechThreshold = userData.speechThreshold ?? vadThreshold.value;
        vadThreshold.value = Math.min(speechThreshold, VAD_THRESHOLD_MAX);
        silenceThreshold.value = userData.speechWaitingTime;
        smartTurnEnabled.value = userData.smartTurnEnabled ?? false;
        energyGateEnabled.value = userData.energyGateEnabled ?? audioDefaults.energyGateEnabled;
        energyGateThresholdDb.value = userData.energyGateThresholdDb ?? audioDefaults.energyGateThresholdDb;
        yield call(migrateLegacyEnergyGateSettings, userData);
        yield put(UserActions.setCurrentUserLoadingStatus(LoadingStatus.Succeeded));
        trackCurrentUserEvent(
          'CurrentUserLoadSucceeded',
          {
            status: response.status,
            requestId: getRequestIdFromHeaders(response.headers)
          },
          getNow() - requestStart
        );
        break;
      }

      default:
        yield put(UserActions.setCurrentUserLoadingStatus(LoadingStatus.Idle));
        break;
    }
  } catch (error) {
    yield put(UserActions.setCurrentUserLoadingStatus(LoadingStatus.Failed));

    const axiosError = error as AxiosError;
    const requestId = getRequestIdFromHeaders(axiosError?.response?.headers) ?? getClientCorrelationId();

    // Log detailed error information to AppInsights
    const errorDetails = {
      status: axiosError?.response?.status,
      statusText: axiosError?.response?.statusText,
      errorCode: axiosError?.code,
      errorMessage: axiosError?.message,
      responseData: axiosError?.response?.data,
      requestUrl: axiosError?.config?.url,
      requestMethod: axiosError?.config?.method
    };

    logger.error('Failed to load current user', errorDetails);
    trackCurrentUserEvent(
      'CurrentUserLoadFailed',
      {
        status: axiosError?.response?.status ?? 0,
        statusText: axiosError?.response?.statusText,
        errorCode: axiosError?.code,
        errorMessage: axiosError?.message,
        isNetworkError: !axiosError?.response,
        requestUrl: axiosError?.config?.url,
        requestMethod: axiosError?.config?.method,
        requestId
      },
      getNow() - requestStart
    );
    yield put(
      UserActions.setCurrentUserLoadError({
        requestId,
        status: axiosError?.response?.status,
        errorCode: axiosError?.code,
        isNetworkError: !axiosError?.response,
        timestamp: new Date().toISOString()
      })
    );

    if (axiosError?.response?.status === StatusCodes.NOT_FOUND) {
      logger.warn('Current user not found (404) - user may not belong to an organization', errorDetails);
      yield put(UserActions.setCurrentUserNotFound());
    } else if (axiosError?.response?.status === StatusCodes.UNAUTHORIZED) {
      logger.warn('Current user unauthorized (401) - authentication may have expired', errorDetails);
      // TODO: Handle unauthorized - logout action not available
      // yield put(UserActions.logout());
    } else if (axiosError?.response?.status === StatusCodes.FORBIDDEN) {
      logger.warn('Current user forbidden (403) - user lacks required permissions', errorDetails);
    } else if (axiosError?.response?.status && axiosError.response.status >= 500) {
      logger.error('Server error while loading current user', errorDetails);
    }
  }
}

function* acceptPermissionForRecords() {
  const response: AxiosResponse<UserDto> = yield call(accountAllowRecord);

  if (response.status == StatusCodes.OK) {
    yield put(UserActions.setCurrentUser(response.data));

    const isHei = typeof window !== 'undefined' && checkIsHeiDomain(window.location.hostname);

    // Redirect to translator for hei.sklls.ai or localhost, otherwise to dashboard
    history.push(isHei ? AppRoutes.Translator : AppRoutes.Dashboard);
  }
}

// function* handleLogout() {
//   try {
//     console.log('Logout TRIGGERED');
//     // Clear user data from Redux store
//     yield put(UserActions.setCurrentUser(null));
//     currentUser.value = null;

//     // Clear any stored tokens or auth state
//     if (typeof window !== 'undefined') {
//       // // Clear localStorage items that might be used for auth
//       // localStorage.removeItem('msal.idtoken');
//       // localStorage.removeItem('msal.account.keys');

//       // Redirect to login page with return URL
//       const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
//       window.location.href = `${AppRoutes.Login}?returnUrl=${returnUrl}`;
//     }
//   } catch (error) {
//     logger.error('Error during logout:', error);
//     // Even if there's an error, still try to redirect to login
//     if (typeof window !== 'undefined') {
//       window.location.href = AppRoutes.Login;
//     }
//   }
// }

function* createUserInvitation(action) {
  const { data, onError, onSuccess } = action.payload;

  try {
    yield call(userCreateOrganizationInvitation, data);

    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    if (onError) {
      // Pass full error to callback for 409 handling
      onError(error);
    }
    logger.log(error);
  }
}

function* inviteMultipleUsers(action) {
  const { data, onError, onSuccess } = action.payload;

  try {
    yield call(userInviteMultipleUsers, data);

    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    if (onError) {
      // Pass full error to callback for 409 handling
      onError(error);
    }
    logger.log(error);
  }
}

function* watchInviteMultipleUsers() {
  yield takeEvery(UserActions.Type.INVITE_MULTIPLE_USERS, inviteMultipleUsers);
}

function* watchCreateUserInvitation() {
  yield takeEvery(UserActions.Type.CREATE_USER_INVITATION, createUserInvitation);
}

function* watchGetCurrentUser() {
  yield takeEvery(UserActions.Type.GET_CURRENT_USER, getCurrentUser);
}

function* watchUserAcceptPermissionForRecords() {
  yield takeEvery(UserActions.Type.ACCEPT_PERMISSION_FOR_RECORD, acceptPermissionForRecords);
}

// function* watchLogout() {
//   yield takeEvery(UserActions.Type.LOGOUT, handleLogout);
// }

export default function* watchUserActionsSaga() {
  yield fork(watchGetCurrentUser);
  yield fork(watchUserAcceptPermissionForRecords);
  yield fork(watchCreateUserInvitation);
  yield fork(watchInviteMultipleUsers);
  // yield fork(watchLogout);
}
